import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Calendar, BookOpen } from 'lucide-react';
import { heroContent, featuredSites } from '../data/mockData';

const Home = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      {/* Hero Section - Visit Iceland Style */}
      <section className="relative h-screen overflow-hidden" data-testid="hero-section">
        {/* Full Background Image - Villa Nabila Johor */}
        <div className="absolute inset-0">
          <img 
            src="/images/villa-nabila-johor-1.jpg" 
            alt="Villa Nabila, Johor - Dark Tourism Malaysia"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-[#1a1c1b]"></div>
        </div>

        {/* Hero Content - Bottom Positioned */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="container-custom pb-20 md:pb-32">
            <div className="max-w-4xl">
              <h1 className="text-white font-light text-5xl md:text-7xl mb-6 leading-tight" data-testid="hero-heading">
                Malaysia's Haunting Heritage Where History Meets Memory
              </h1>
              <p className="text-lg md:text-xl font-light text-white/90 mb-8 leading-relaxed max-w-2xl">
                If night tours or "haunted" narratives are offered, keep conduct dignified—these stories often root in real suffering.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/itineraries" className="btn-primary" data-testid="plan-trip-btn">
                  Plan your trip <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Spotlight Section with Penang War Museum */}
      <section className="relative bg-[#0f0f10] py-32" data-testid="spotlight-section">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="heading-1 text-[#dc2626] mb-8">
              Explore Haunted Places in Malaysia
            </h2>
            <p className="body-large text-white/90 mb-6">
              Documenting places across Malaysia connected to memory, tragedy, conflict, and historical depth. 
              Over 250 sites preserving histories that shaped the nation.
            </p>
            <p className="body-medium text-white/70 mb-10">
              After more than a decade in academia, research identified over 250 sites across Malaysia—
              many of which remain largely unknown or are quietly fading from national consciousness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/dark-tourism-explained" className="btn-primary" data-testid="what-is-dark-tourism-btn">
                What is Dark Tourism? <BookOpen className="ml-2" size={20} />
              </Link>
              <Link to="/about" className="btn-secondary" data-testid="mission-btn">
                Mission
              </Link>
            </div>
          </div>
        </div>

        {/* Penang War Museum Image with "(Places Remember)" tagline */}
        <div className="mt-16 relative">
          <img 
            src="/images/penang-war-museum-1.jpg"
            alt="Penang War Museum"
            className="w-full h-[500px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="places-remember-text">
            <p className="text-white/40 text-sm uppercase tracking-[0.5em] mb-4 font-medium">The places we forget still carry what we cannot</p>
            <h2 className="text-white text-5xl md:text-8xl font-bold tracking-[0.15em] leading-none">
              Places
            </h2>
            <div className="w-24 h-[1px] bg-[#dc2626] my-4"></div>
            <h2 className="text-[#dc2626] text-5xl md:text-8xl font-bold tracking-[0.15em] leading-none">
              Remember
            </h2>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="heading-2 text-[#dc2626] mb-8">
              Understanding Malaysia's Dark Tourism Heritage
            </h2>
            <p className="body-medium text-white/90 mb-6">
              Dark tourism refers to travel connected to places shaped by tragedy, conflict, or loss, 
              where visitors seek to understand the past and reflect on its meaning.
            </p>
            <p className="body-medium text-white/80 mb-8">
              After more than a decade in academia, our research identified over 250 sites across 
              Malaysia connected to memory, loss, and historical depth—many of which remain largely 
              unknown or are quietly fading from national consciousness.
            </p>
            <Link to="/dark-tourism-explained" className="btn-secondary">
              What is Dark Tourism? <BookOpen className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Sites Grid */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-6xl">
            <div className="mb-16">
              <h2 className="heading-1 text-[#dc2626] mb-4">
                Featured Sites
              </h2>
              <p className="body-medium text-white/70 max-w-3xl">
                Explore Malaysia's most significant dark tourism locations
              </p>
            </div>
          
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredSites.map((site) => (
                <div 
                  key={site.id}
                  className="site-card rounded-lg overflow-hidden group cursor-pointer"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={site.image}
                      alt={site.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="caption text-[#dc2626] mb-2 block">{site.category}</span>
                      <h3 className="heading-4 text-white">{site.name}</h3>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3 text-[#888680]">
                      <MapPin size={16} />
                      <span className="body-small">{site.location}</span>
                    </div>
                    <p className="body-small text-white/70 mb-4 line-clamp-3">
                      {site.description}
                    </p>
                    <Link 
                      to={`/explore/${site.id}`}
                      className="text-[#dc2626] hover:text-[#dc2626]/80 font-medium inline-flex items-center transition-colors"
                    >
                      Learn More <ArrowRight className="ml-2" size={16} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-start mt-12">
              <Link to="/explore" className="btn-primary">
                View All Sites <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Sections */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-6xl">
            <div className="grid md:grid-cols-3 gap-8">
            {/* Dark Tourism Explained */}
            <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222] hover:border-[#dc2626]/50 transition-colors">
              <BookOpen className="text-[#dc2626] mb-4" size={40} />
              <h3 className="heading-4 text-white mb-4">Dark Tourism Explained</h3>
              <p className="body-small text-white/70 mb-6">
                Understand the history, typologies, and cultural significance of dark tourism in Malaysia.
              </p>
              <Link to="/dark-tourism-explained" className="text-[#dc2626] hover:text-[#dc2626]/80 font-medium inline-flex items-center">
                Learn More <ArrowRight className="ml-2" size={16} />
              </Link>
            </div>

            {/* Guidelines */}
            <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222] hover:border-[#dc2626]/50 transition-colors">
              <Calendar className="text-[#dc2626] mb-4" size={40} />
              <h3 className="heading-4 text-white mb-4">Visitor Guidelines</h3>
              <p className="body-small text-white/70 mb-6">
                Learn how to visit dark tourism sites with respect, cultural sensitivity, and awareness.
              </p>
              <Link to="/guidelines" className="text-[#dc2626] hover:text-[#dc2626]/80 font-medium inline-flex items-center">
                Read Guidelines <ArrowRight className="ml-2" size={16} />
              </Link>
            </div>

            {/* Share Your Story */}
            <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222] hover:border-[#dc2626]/50 transition-colors">
              <MapPin className="text-[#dc2626] mb-4" size={40} />
              <h3 className="heading-4 text-white mb-4">Share Your Story</h3>
              <p className="body-small text-white/70 mb-6">
                Have you visited a site or know a story? Help us preserve Malaysia's heritage.
              </p>
              <Link to="/share-story" className="text-[#dc2626] hover:text-[#dc2626]/80 font-medium inline-flex items-center">
                Contribute <ArrowRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Before It Disappears Section */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="heading-2 text-[#dc2626] mb-8">
              Before It Disappears
            </h2>
            <p className="body-large text-white/90 mb-6">
              Across Malaysia, traces of earlier lives and difficult histories are slowly disappearing from view.
            </p>
            <p className="body-medium text-white/70 mb-8">
              Some have already vanished. Others remain but are fading quietly into the landscape. 
              WWII pillboxes crumble on Kelantan's coast. Pudu Jail was demolished in 2012. 
              Bidong Island's refugee structures collapse. Burial grounds become overgrown.
            </p>
            <p className="body-medium text-white/80 italic mb-12 border-l-4 border-[#dc2626] pl-6">
              "When these places fade, it is not only names on gravestones that disappear. 
              Whole chapters of lived experience begin to slip away."
            </p>
            <Link to="/about" className="btn-primary" data-testid="mission-link">
              Mission <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
